import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useBatchDashboardAnalytics } from './useBatchDashboardAnalytics';
import type {
    AnalyticsGranularity,
    AggregatedValuesResponse,
    NetWorthAnalyticsData,
    NetWorthPeriodItem,
    NetWorthSummary,
    NetWorthChange,
    ChartPeriod,
    PeriodAvailability,
    NetWorthProjectionItem,
} from '../types';

/**
 * Determine automatic granularity based on data span.
 * Monthly if span < 365 days, yearly otherwise.
 */
function determineGranularity(assetsStart: string | undefined, debtsStart: string | undefined): AnalyticsGranularity {
    // Use earliest start date from both responses
    const dates: Date[] = [];
    if (assetsStart) dates.push(new Date(assetsStart));
    if (debtsStart) dates.push(new Date(debtsStart));

    if (dates.length === 0) {
        return 'month'; // Default to monthly if no data
    }

    const earliestDate = new Date(Math.min(...dates.map(d => d.getTime())));
    const now = new Date();
    const daysDiff = (now.getTime() - earliestDate.getTime()) / (1000 * 60 * 60 * 24);

    return daysDiff >= 365 ? 'year' : 'month';
}

/**
 * Filters items to only include those within the specified period.
 * For max/future periods, returns all items unfiltered.
 */
function filterItemsByPeriod(
    items: NetWorthPeriodItem[],
    period: ChartPeriod
): NetWorthPeriodItem[] {
    if (period === 'max' || period === 'future') {
        return items;
    }

    const now = new Date();
    let monthsBack: number;

    switch (period) {
        case '3m':
            monthsBack = 3;
            break;
        case '6m':
            monthsBack = 6;
            break;
        case '1y':
            monthsBack = 12;
            break;
        default:
            return items;
    }

    const cutoffDate = new Date(now);
    cutoffDate.setMonth(cutoffDate.getMonth() - monthsBack);
    // Set to start of that month
    cutoffDate.setDate(1);
    cutoffDate.setHours(0, 0, 0, 0);

    return items.filter(item => {
        const itemDate = new Date(item.periodEnd);
        return itemDate >= cutoffDate;
    });
}

/**
 * Calculates period availability based on the earliest data date.
 * - 3m: Available if data span >= 3 months
 * - 6m: Available if data span >= 6 months
 * - 1y: Available if data span >= 12 months
 * - Max: Always available
 * - Future: Available when the data spans enough calendar years — this
 *   mirrors the backend gate, which counts yearly aggregate items, NOT a
 *   rolling 24-month window. Data since July 2024 spans 3 calendar years in
 *   2026 and is projectable long before 24 months have elapsed.
 */
function calculatePeriodAvailability(
    earliestDataDate: Date | null,
    minimumYearsForProjection: number = 2,
    now: Date = new Date()
): PeriodAvailability {
    if (!earliestDataDate) {
        return {
            can3m: false,
            can6m: false,
            can1y: false,
            canFuture: false,
            futureUnavailableReason: 'No data available',
        };
    }

    const monthsDiff = (now.getFullYear() - earliestDataDate.getFullYear()) * 12 +
        (now.getMonth() - earliestDataDate.getMonth());

    const calendarYearSpan = now.getFullYear() - earliestDataDate.getFullYear() + 1;
    const canFuture = calendarYearSpan >= minimumYearsForProjection;

    return {
        can3m: monthsDiff >= 3,
        can6m: monthsDiff >= 6,
        can1y: monthsDiff >= 12,
        canFuture,
        futureUnavailableReason: !canFuture
            ? `Future projections require at least ${minimumYearsForProjection} years of historical data`
            : undefined,
    };
}

/**
 * Determines if projection data should be fetched.
 */
function shouldFetchProjection(period: ChartPeriod, canFuture: boolean): boolean {
    return period === 'future' && canFuture;
}

/**
 * Get the earliest data date from assets and debts responses.
 */
function getEarliestDataDate(assetsStart: string | undefined, debtsStart: string | undefined): Date | null {
    const dates: Date[] = [];
    if (assetsStart) dates.push(new Date(assetsStart));
    if (debtsStart) dates.push(new Date(debtsStart));

    if (dates.length === 0) {
        return null;
    }

    return new Date(Math.min(...dates.map(d => d.getTime())));
}

/**
 * Merge assets and debts data by aligning periods.
 * Handles cases where one type has data but the other does not.
 */
function mergePeriodsData(
    assetsData: AggregatedValuesResponse | undefined,
    debtsData: AggregatedValuesResponse | undefined
): NetWorthPeriodItem[] {
    // Create maps for quick lookup
    const assetsMap = new Map(
        (assetsData?.items || []).map(item => [item.periodLabel, item])
    );
    const debtsMap = new Map(
        (debtsData?.items || []).map(item => [item.periodLabel, item])
    );

    // Get all unique period labels, preserving order from assets (or debts if no assets)
    const allPeriodLabels = new Set<string>();
    (assetsData?.items || []).forEach(item => allPeriodLabels.add(item.periodLabel));
    (debtsData?.items || []).forEach(item => allPeriodLabels.add(item.periodLabel));

    // Convert to array and sort by periodEnd date
    const sortedPeriods = Array.from(allPeriodLabels).sort((a, b) => {
        const aItem = assetsMap.get(a) || debtsMap.get(a);
        const bItem = assetsMap.get(b) || debtsMap.get(b);
        if (!aItem || !bItem) return 0;
        return new Date(aItem.periodEnd).getTime() - new Date(bItem.periodEnd).getTime();
    });

    // Merge data for each period (first pass - base values)
    const baseItems = sortedPeriods.map(periodLabel => {
        const assetItem = assetsMap.get(periodLabel);
        const debtItem = debtsMap.get(periodLabel);

        // Use periodEnd from whichever item exists
        const periodEnd = assetItem?.periodEnd || debtItem?.periodEnd || '';

        const assetsValue = assetItem?.totalValue || 0;
        const debtsValue = debtItem?.totalValue || 0;

        return {
            periodEnd,
            periodLabel,
            assetsValue,
            debtsValue,
            netWorthValue: assetsValue - debtsValue,
            assetLedgerCount: assetItem?.ledgerCount || 0,
            debtLedgerCount: debtItem?.ledgerCount || 0,
        };
    });

    // Second pass - calculate changes from previous period
    return baseItems.map((item, index) => {
        if (index === 0) {
            // First item has no previous to compare
            return {
                ...item,
                netWorthChange: null,
                netWorthChangePercent: null,
                assetsChange: null,
                assetsChangePercent: null,
                debtsChange: null,
                debtsChangePercent: null,
            };
        }

        const prev = baseItems[index - 1];
        if (!prev) {
            return {
                ...item,
                netWorthChange: null,
                netWorthChangePercent: null,
                assetsChange: null,
                assetsChangePercent: null,
                debtsChange: null,
                debtsChangePercent: null,
            };
        }

        return {
            ...item,
            netWorthChange: item.netWorthValue - prev.netWorthValue,
            netWorthChangePercent: calculatePercentageChange(item.netWorthValue, prev.netWorthValue),
            assetsChange: item.assetsValue - prev.assetsValue,
            assetsChangePercent: calculatePercentageChange(item.assetsValue, prev.assetsValue),
            debtsChange: item.debtsValue - prev.debtsValue,
            debtsChangePercent: calculatePercentageChange(item.debtsValue, prev.debtsValue),
        };
    });
}

/**
 * Calculate percentage change between two values.
 *
 * CONTRACT: This is the canonical implementation for percentage calculations.
 * All backend implementations (PHP, Go) MUST use the same formula.
 * See: api/src/Domain/Config/AppConfig.php for full contract documentation.
 *
 * Formula: ((currentValue - previousValue) / |previousValue|) * 100
 *
 * Edge cases when previousValue === 0:
 *   - currentValue > 0:  return 100 (treat as 100% increase from zero)
 *   - currentValue < 0:  return -100 (treat as 100% decrease from zero)
 *   - currentValue === 0: return null (no change, avoid 0/0)
 */
function calculatePercentageChange(currentValue: number, previousValue: number): number | null {
    if (previousValue === 0) {
        // Edge cases per contract documentation
        if (currentValue > 0) return 100;
        if (currentValue < 0) return -100;
        return null; // Both are 0
    }

    return ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
}

/**
 * Calculate YTD change for a metric.
 * Works with both monthly and yearly granularity:
 * - Monthly: compares current month to December of prior year
 * - Yearly: compares current year to prior year (which represents Jan 1 starting point)
 */
function calculateChanges(
    items: NetWorthPeriodItem[],
    getValue: (item: NetWorthPeriodItem) => number,
    granularity: AnalyticsGranularity
): { currentValue: number; ytdChange: NetWorthChange | null } {
    if (items.length === 0) {
        return { currentValue: 0, ytdChange: null };
    }

    const currentItem = items[items.length - 1];
    if (!currentItem) {
        return { currentValue: 0, ytdChange: null };
    }

    const currentValue = getValue(currentItem);
    let ytdChange: NetWorthChange | null = null;

    const currentDate = new Date(currentItem.periodEnd);
    const currentYear = currentDate.getFullYear();

    // Find previous year end value for YTD
    const prevYearItem = items.find(item => {
        const itemDate = new Date(item.periodEnd);
        const itemYear = itemDate.getFullYear();

        if (granularity === 'month') {
            // Find December of previous year
            return itemDate.getMonth() === 11 && itemYear === currentYear - 1;
        } else {
            // Find previous year in yearly view (this is effectively Jan 1 of current year)
            return itemYear === currentYear - 1;
        }
    });

    if (prevYearItem) {
        const prevValue = getValue(prevYearItem);
        const amount = currentValue - prevValue;
        ytdChange = {
            amount,
            percentage: calculatePercentageChange(currentValue, prevValue),
        };
    }

    return { currentValue, ytdChange };
}

/**
 * Build summary data for all three metrics.
 */
function buildSummaries(
    items: NetWorthPeriodItem[],
    granularity: AnalyticsGranularity
): { netWorthSummary: NetWorthSummary; assetsSummary: NetWorthSummary; debtsSummary: NetWorthSummary } {
    const assetsData = calculateChanges(items, item => item.assetsValue, granularity);
    const debtsData = calculateChanges(items, item => item.debtsValue, granularity);
    const netWorthData = calculateChanges(items, item => item.netWorthValue, granularity);

    return {
        netWorthSummary: {
            currentValue: netWorthData.currentValue,
            ytdChange: netWorthData.ytdChange,
        },
        assetsSummary: {
            currentValue: assetsData.currentValue,
            ytdChange: assetsData.ytdChange,
        },
        debtsSummary: {
            currentValue: debtsData.currentValue,
            ytdChange: debtsData.ytdChange,
        },
    };
}

/**
 * Convert projection items to chart item format (NetWorthPeriodItem plus the
 * isProjected flag). Projected items don't have change data (not meaningful to
 * compare speculative values).
 *
 * Shared by the hook (for summaries/hasDebts) and the dashboard chart so the
 * mapping lives in exactly one place.
 */
export function convertProjectionToChartItems(
    projectionItems: NetWorthProjectionItem[],
): (NetWorthPeriodItem & { isProjected?: boolean })[] {
    return projectionItems.map(item => ({
        periodEnd: item.periodEnd,
        periodLabel: item.periodLabel,
        isProjected: item.isProjected,
        assetsValue: item.assetsValue ?? 0,
        debtsValue: item.debtsValue ?? 0,
        netWorthValue: item.netWorthValue,
        assetLedgerCount: item.assetLedgerCount ?? 0,
        debtLedgerCount: item.debtLedgerCount ?? 0,
        // Projected items don't show changes
        netWorthChange: null,
        netWorthChangePercent: null,
        assetsChange: null,
        assetsChangePercent: null,
        debtsChange: null,
        debtsChangePercent: null,
    }));
}

/**
 * Check if user has any debt data.
 * Returns true if any period has debts > 0 or debt ledger count > 0.
 */
function checkHasDebts(items: NetWorthPeriodItem[]): boolean {
    return items.some(item => item.debtsValue > 0 || item.debtLedgerCount > 0);
}

interface UseNetWorthAnalyticsOptions {
    /** User-selected granularity (M/Y toggle). Required for independent control. */
    granularity: AnalyticsGranularity;
    /** Chart period for time range selection. */
    period?: ChartPeriod;
    /** Optional override for projection return rate (decimal, e.g., 0.07 = 7%) */
    projectionReturnRate?: number | null;
    /** Optional override for projection annual contribution amount */
    projectionContribution?: number | null;
    /** Optional override for projection yearly debt change (negative = paydown) */
    projectionDebtChange?: number | null;
    /** Number of years to project into the future (uses server default if not specified) */
    projectionYears?: number;
    /** Whether FI mode is on; when false NO FI params are sent */
    projectionFiEnabled?: boolean;
    /** Optional annual expenses for FI target; null = no FI line */
    projectionAnnualExpenses?: number | null;
    /** Collection IDs counted toward FI; null/empty = all included */
    projectionFiCollectionIds?: number[] | null;
    /** FI multiplier (100 / withdrawal rate); undefined = server default */
    projectionFiMultiplier?: number;
    /** FI-specific override for the investable bucket return rate (decimal); null = auto-derived */
    projectionInvestableReturnRate?: number | null;
    /** Whether the post-FI withdrawal simulation is on; off = no new params sent */
    projectionSimulateWithdrawal?: boolean;
    /** Inflation-rate override for the real-terms simulation (decimal); null = server default */
    projectionInflationRate?: number | null;
}

/**
 * Hook for fetching and computing Net Worth analytics data.
 * Calls aggregated-values endpoint twice in parallel (assets + debts),
 * merges the data, and calculates MTD/YTD changes.
 *
 * Features:
 * - User-controlled granularity (M/Y toggle)
 * - Period-based time range selection (3m, 6m, 1y, Max, Future)
 * - Independent control: period filters data range, granularity controls display density
 * - Period alignment across assets and debts
 * - Net Worth calculation per period (assets - debts)
 * - MTD/YTD change calculations with edge case handling
 * - Future mode fetches from projection endpoint
 * - hasDebts flag for conditional dual Y-axis rendering
 */
export function useNetWorthAnalytics(options: UseNetWorthAnalyticsOptions) {
    const { granularity, period = 'max', projectionReturnRate, projectionContribution, projectionDebtChange, projectionYears, projectionFiEnabled, projectionAnnualExpenses, projectionFiCollectionIds, projectionFiMultiplier, projectionInvestableReturnRate, projectionSimulateWithdrawal, projectionInflationRate } = options;
    const { config } = useAuth();
    const minimumYearsForProjection = config?.analytics.minimumYearsForProjection ?? 2;

    // Single batch request for all dashboard analytics data
    const batchQuery = useBatchDashboardAnalytics({
        granularity,
        period,
        projectionReturnRate,
        projectionContribution,
        projectionDebtChange,
        projectionYears,
        projectionFiEnabled,
        projectionAnnualExpenses,
        projectionFiCollectionIds,
        projectionFiMultiplier,
        projectionInvestableReturnRate,
        projectionSimulateWithdrawal,
        projectionInflationRate,
    });

    // Calculate earliest data date for period availability
    const earliestDataDate = useMemo(() => {
        if (!batchQuery.data) return null;
        return getEarliestDataDate(
            batchQuery.data.assets.start,
            batchQuery.data.debts.start
        );
    }, [batchQuery.data]);

    // Calculate period availability
    const periodAvailability = useMemo(() => {
        return calculatePeriodAvailability(earliestDataDate, minimumYearsForProjection);
    }, [earliestDataDate, minimumYearsForProjection]);

    // Determine auto granularity based on data span (for reporting purposes)
    const autoGranularity = useMemo(() => {
        if (!batchQuery.data) return 'month' as AnalyticsGranularity;
        return determineGranularity(
            batchQuery.data.assets.start,
            batchQuery.data.debts.start
        );
    }, [batchQuery.data]);

    // Determine if projection data should be used
    const shouldUseProjection = period === 'future' && batchQuery.data?.projection?.canProject === true;

    // Build summaries using the current granularity data
    // YTD adapts based on granularity: monthly compares to Dec, yearly compares to prior year
    const summaries = useMemo(() => {
        if (!batchQuery.data) return null;

        const items = mergePeriodsData(batchQuery.data.assets, batchQuery.data.debts);

        if (items.length === 0) {
            return {
                netWorthSummary: { currentValue: 0, ytdChange: null },
                assetsSummary: { currentValue: 0, ytdChange: null },
                debtsSummary: { currentValue: 0, ytdChange: null },
            };
        }

        return buildSummaries(items, granularity);
    }, [batchQuery.data, granularity]);

    // Chart data is now the same as summary data (single granularity)
    const assetsChartData = batchQuery.data?.assets;
    const debtsChartData = batchQuery.data?.debts;

    // Compute chart data based on selected granularity and period
    const data = useMemo((): NetWorthAnalyticsData | null => {
        // Need summaries to proceed
        if (!summaries || !batchQuery.data) {
            return null;
        }

        // For Future mode, use projection data
        if (shouldUseProjection && batchQuery.data.projection) {
            const projectionItems = convertProjectionToChartItems(batchQuery.data.projection.items);

            return {
                items: projectionItems,
                ...summaries,
                granularity: granularity,
                currency: batchQuery.data.projection.currency,
                totalAssetLedgers: assetsChartData?.ledgerCount || 0,
                totalDebtLedgers: debtsChartData?.ledgerCount || 0,
            };
        }

        // Need at least one response for chart data
        if (!assetsChartData && !debtsChartData) {
            return null;
        }

        let items = mergePeriodsData(assetsChartData, debtsChartData);

        // Apply period filtering
        items = filterItemsByPeriod(items, period);

        // If no items, return minimal data structure with summaries
        if (items.length === 0) {
            return {
                items: [],
                ...summaries,
                granularity: granularity,
                currency: assetsChartData?.currency || debtsChartData?.currency || 'USD',
                totalAssetLedgers: assetsChartData?.ledgerCount || 0,
                totalDebtLedgers: debtsChartData?.ledgerCount || 0,
            };
        }

        return {
            items,
            ...summaries,
            granularity: granularity,
            currency: assetsChartData?.currency || debtsChartData?.currency || 'USD',
            totalAssetLedgers: assetsChartData?.ledgerCount || 0,
            totalDebtLedgers: debtsChartData?.ledgerCount || 0,
        };
    }, [
        assetsChartData,
        debtsChartData,
        granularity,
        summaries,
        period,
        shouldUseProjection,
        batchQuery.data,
    ]);

    // Calculate hasDebts flag
    const hasDebts = useMemo(() => {
        if (data?.items) {
            return checkHasDebts(data.items);
        }
        return false;
    }, [data?.items]);

    return {
        data,
        isLoading: batchQuery.isLoading,
        isFetching: batchQuery.isFetching,
        isError: batchQuery.isError,
        error: batchQuery.error,
        refetch: batchQuery.refetch,
        /** The automatically determined granularity based on data span */
        autoGranularity,
        /** Whether yearly granularity is available (data span >= 365 days) */
        canUseYearlyGranularity: autoGranularity === 'year',
        /** Period availability flags */
        periodAvailability,
        /** Earliest data date for debugging/display purposes */
        earliestDataDate,
        /** Projection-specific data (only available when period === 'future') */
        projectionData: shouldUseProjection ? batchQuery.data?.projection : null,
        /** Whether user has any debt data (for conditional dual Y-axis) */
        hasDebts,
    };
}

// Export utility functions for testing (original testUtils)
export const testUtils = {
    determineGranularity,
    mergePeriodsData,
    calculatePercentageChange,
    calculateChanges,
    buildSummaries,
};

// Export period-related utility functions for testing
export const periodTestUtils = {
    filterItemsByPeriod,
    calculatePeriodAvailability,
    shouldFetchProjection,
    getEarliestDataDate,
    checkHasDebts,
};
